import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSWRConfig } from 'swr'
import { match } from 'ts-pattern'

import { Button } from '@frame/ui-primitives/button'
import { P } from '@frame/ui-primitives/p'

import { useVerifyTask } from '@frame/components/screens/tasks/hooks/useVerifyTask'
import { EventScope, InternalEventType } from '@frame/lib/api/events/types'
import { buildExitVerificationViewProps, withEmployerScope, withSessionScope } from '@frame/lib/api/events/utils'
import { TrueworkJsStrategy } from '@frame/lib/api/tasks/types'
import { useRootContext } from '@frame/lib/context'
import { useDirectTask } from '@frame/lib/hooks/useDirectTask'
import { useEmitComplete } from '@frame/lib/hooks/useEmitComplete'
import { useEventHandler } from '@frame/lib/hooks/useEventHandler'
import { useStore } from '@frame/lib/stores/useStore'
import { routes } from '@frame/routes'
import { DataAttribute } from '@frame/types'

import { ExitPromptType } from '.'
import { LoadingSpinner } from '../loading-spinner'

type Props = {
  type: ExitPromptType
  hide: (event: React.MouseEvent) => void
}

export function ExitPromptButtons({ type, hide }: Props) {
  const verifierName = useStore((state) => state.session?.originating_party_display_name ?? 'the verifier')
  const { api, events, actions } = useRootContext()
  const navigate = useNavigate()
  const location = useLocation()
  const { handleEvent } = useEventHandler()
  const emitComplete = useEmitComplete()
  const [isLoading, setIsLoading] = useState(false)
  const { mutate } = useSWRConfig()
  const task = useDirectTask(true)
  const { handleVerify } = useVerifyTask()

  async function handleSubmit(scope: EventScope, event: React.MouseEvent) {
    setIsLoading(true)
    // event propagation needs to stop to prevent the parent (ExitPrompt) from publishing a cancel event
    event.stopPropagation()
    handleEvent(
      InternalEventType.EXIT_VERIFICATION_SUBMIT_FINISH,
      buildExitVerificationViewProps(scope, location.pathname, type),
      emitComplete
    )
    await api.clientSession.completeSession()
    navigate(routes.complete)
  }

  function handleFinishLater(scope: EventScope, event: React.MouseEvent) {
    event.stopPropagation()
    handleEvent(
      InternalEventType.EXIT_VERIFICATION_SUBMIT_FINISH_LATER,
      buildExitVerificationViewProps(scope, location.pathname, type),
      () => {
        events.emit('close')
      }
    )
  }

  function handleStartOver(scope: EventScope, event: React.MouseEvent) {
    event.stopPropagation()
    handleEvent(
      InternalEventType.EXIT_VERIFICATION_SUBMIT_START_OVER,
      buildExitVerificationViewProps(scope, location.pathname, type)
    )
    navigate(routes.root)
  }

  async function handleSkipToCredentials() {
    if (!task) {
      throw new Error('No task found to skip to credentials')
    }
    const preparedTask = await actions.addStrategyToDirectTask({
      strategy: TrueworkJsStrategy.CREDENTIALS,
      directTaskId: task.id,
    })
    await mutate(['all', 'tasks'])
    if (!preparedTask) {
      throw new Error('Failed to add credentials strategy to task')
    }
    handleVerify(preparedTask)
  }

  return match(type)
    .with(ExitPromptType.SESSION, () => (
      <div className="space-y-4">
        <Button
          data-cy={DataAttribute.EXIT_PROMPT_FINISH_LATER}
          disabled={isLoading}
          onClick={withSessionScope(handleFinishLater)}
          applyCobranding
        >
          I'll finish it later
        </Button>
        <Button
          data-cy={DataAttribute.EXIT_PROMPT_SUBMIT}
          disabled={isLoading}
          Icon={isLoading ? LoadingSpinner : undefined}
          onClick={withSessionScope(handleSubmit)}
          intent="secondary"
        >
          I'm done, submit
        </Button>
      </div>
    ))
    .with(ExitPromptType.EMPLOYER, () => (
      <div className="space-y-4">
        <Button
          data-cy={DataAttribute.EXIT_PROMPT_START_OVER}
          disabled={isLoading}
          onClick={withEmployerScope(handleStartOver)}
          applyCobranding
        >
          Start over
        </Button>
        <Button
          data-cy={DataAttribute.EXIT_PROMPT_FINISH_LATER}
          disabled={isLoading}
          onClick={withEmployerScope(handleFinishLater)}
          intent="secondary"
        >
          I'll finish later
        </Button>
        <Button
          data-cy={DataAttribute.EXIT_PROMPT_SUBMIT}
          onClick={withEmployerScope(handleSubmit)}
          disabled={isLoading}
          intent="secondary"
          border={false}
        >
          I'm done, submit
        </Button>
      </div>
    ))
    .with(ExitPromptType.INCOMPLETE, () => (
      <div>
        <P>
          You cannot return after submitting. {verifierName[0].toUpperCase() + verifierName.slice(1)} may need you to
          verify another way.
        </P>
        <div className="mt-5 space-y-4">
          <Button
            data-cy={DataAttribute.EXIT_PROMPT_FINISH_LATER}
            Icon={isLoading ? LoadingSpinner : undefined}
            disabled={isLoading}
            onClick={withEmployerScope(handleSubmit)}
            applyCobranding
          >
            Submit
          </Button>
          <Button data-cy={DataAttribute.EXIT_PROMPT_START_OVER} disabled={isLoading} onClick={hide} intent="secondary">
            Back
          </Button>
        </div>
      </div>
    ))
    .with(ExitPromptType.VERIFY_INCOME_LOADING, () => (
      <div>
        <Button data-cy={DataAttribute.EXIT_PROMPT_FINISH_LATER} onClick={handleSkipToCredentials} applyCobranding>
          Connect payroll to verify
        </Button>
      </div>
    ))
    .exhaustive()
}
